import { useEffect, useRef, useState } from "react";
import { useQuill } from "react-quilljs";
// import Quill, { DeltaStatic, Sources } from "quill";
// import "react-quill/dist/quill.snow.css";
import "quill/dist/quill.snow.css"; // Add css for snow theme
import { useFormikContext } from "formik";
import styled, { css } from "styled-components";
import FieldWrapper from "./FieldWrapper";
import { defaultBoxShadow } from "../../assetsConfig";
import {
	htmlToPlainTextContent,
	RICHTEXT_QUILL_FORMATS,
	RICHTEXT_QUILL_TOOLBAR_MODULES,
	sanitizeRichTextForStorage,
	validateRichTextToolbarFormats,
} from "../../utils/richTextFieldConstraints";

const RichTextCharLimit = (props: {
	currentLength: number;
	maxLength?: number;
}) => {
	const { currentLength, maxLength } = props;

	if (maxLength && currentLength) {
		const warning = currentLength >= maxLength * 0.85 ? "text-colorDanger" : "";

		return currentLength ? (
			<span className={`absolute right-[.5rem] bottom-[-28px] ${warning}`}>
				{currentLength}/{maxLength}
			</span>
		) : (
			<></>
		);
	} else return <></>;
};

const StyledRichTextField = styled.div<{ height?: string; readOnly?: boolean }>`
	box-shadow: ${defaultBoxShadow};
	font-family: ${({ theme }) =>
		`${theme.fontCopy.fontFamily}${theme.fontCopy.fontFallback ? `, ${theme.fontCopy.fontFallback}` : ""}`} !important;
	font-size: ${({ theme }) => theme.pSize} !important;
	background: ${({ theme }) => theme.colorBackgroundLightLight};

	ul,
	ol,
	li {
		font-family: ${({ theme }) =>
			`${theme.fontCopy.fontFamily}${theme.fontCopy.fontFallback ? `, ${theme.fontCopy.fontFallback}` : ""}`} !important;
		font-size: ${({ theme }) => theme.pSize} !important;
	}

	ul,
	ol {
		padding-left: 0;
	}

	.ql-formats {
		margin-right: 0 !important;
	}

	.ql-snow {
		border: 0 !important;
	}

	${(p) =>
		p.readOnly &&
		css`
			background: ${({ theme }) => theme.colorFieldReadOnly};
		`}

	.ql-editor {
		/* allow vertical resizing */
		overflow-y: scroll;
		resize: vertical;

		&.ql-editor.ql-blank::before {
			font-family: ${({ theme }) =>
				`${theme.fontCopy.fontFamily}${theme.fontCopy.fontFallback ? `, ${theme.fontCopy.fontFallback}` : ""}`} !important;
			color: ${({ theme }) => theme.colorCopyLight} !important;
			font-style: normal;
			font-size: ${({ theme }) => theme.pSize};
		}
		${(p) =>
			p.height &&
			css`
				height: ${p.height};
			`}
	}
`;

const RichTextField = (props: RichTextFieldProps) => {
	const { setFieldValue, setFieldError } = useFormikContext<any>();
	const [charCount, setCharCount] = useState<number>(0);
	const [isTouched, setIsTouched] = useState(false);

	const modules = {
		toolbar: props.readOnly ? false : [...RICHTEXT_QUILL_TOOLBAR_MODULES],
		history: {
			delay: 500,
			maxStack: 100,
			userOnly: true,
		},
	};
	const formats = [...RICHTEXT_QUILL_FORMATS];

	const { quill, quillRef } = useQuill({
		modules: modules,
		formats: formats,
		placeholder: props.placeholder,
	});

	useEffect(() => {
		if (quill) {
			quill.on("text-change", (delta, oldDelta, source) => {
				setIsTouched(true);

				// update word count on text change
				const text = quill.getText();
				const len = text.replace(/<(.|\n)*?>/g, "").trim().length;
				setCharCount(len);

				// trim text after character limit
				if (props.maxLength && len > props.maxLength) {
					quill.deleteText(props.maxLength, len + 1);
				}

				if (len === 0) {
					setFieldValue(props.name, "");
					setFieldError(props.name, "");
				} else {
					const html = quill.root.innerHTML;
					const formatErr = validateRichTextToolbarFormats(
						html,
						quill.getText(),
					);
					const stored = sanitizeRichTextForStorage(html);
					setFieldValue(props.name, stored);
					setFieldError(props.name, formatErr || "");
				}
			});

			// trigger onblur event and pass sanitized html up
			quill.on("selection-change", (range, oldRange, source) => {
				const onblur = range === null && oldRange !== null;
				if (onblur) {
					const html = quill.root.innerHTML;
					const text = quill.getText();
					const tlen = text.replace(/<(.|\n)*?>/g, "").trim().length;
					if (tlen === 0) {
						setFieldError(props.name, "");
						!props.readOnly && props.onBlur && props.onBlur("");
						return;
					}
					const formatErr = validateRichTextToolbarFormats(
						html,
						quill.getText(),
					);
					if (formatErr) {
						setFieldError(props.name, formatErr);
						return;
					}
					setFieldError(props.name, "");
					const sanitizedHtmlContent = sanitizeRichTextForStorage(html);
					!props.readOnly && props.onBlur && props.onBlur(sanitizedHtmlContent);
				}
			});
		}
	}, [quill, props.readOnly]);

	useEffect(() => {
		if (quill) {
			quill.enable(!props.readOnly);
		}
	}, [quill, props.readOnly]);

	// populate initial value
	useEffect(() => {
		if (quill && props.value && !isTouched) {
			// set touched to true so the value doesn't repopulate
			setIsTouched(true);

			const rawVal = props.value || "";
			const formatErr = validateRichTextToolbarFormats(
				rawVal,
				htmlToPlainTextContent(rawVal),
			);
			const sanitizedHtmlContent = sanitizeRichTextForStorage(rawVal);
			setFieldError(props.name, formatErr || "");
			setFieldValue(props.name, sanitizedHtmlContent);

			const delta = quill.clipboard.convert({ html: sanitizedHtmlContent });
			quill.setContents(delta, "silent");

			// set initial word count
			const text = quill.getText();
			const len = text.replace(/<(.|\n)*?>/g, "").trim().length;
			setCharCount(len);
		}
	}, [quill, props.value, isTouched]);

	return (
		<FieldWrapper
			className={props.className}
			name={props.name}
			success={props.success}
			error={props.error}
		>
			{(success, error) => (
				<>
					<StyledRichTextField
						{...(props.height && {
							height: props.height,
						})}
						readOnly={props.readOnly}
					>
						<div ref={quillRef} />
					</StyledRichTextField>
					<RichTextCharLimit
						currentLength={charCount}
						maxLength={props.maxLength}
					/>
					{/* <div ref={counterRef} /> */}
				</>
			)}
		</FieldWrapper>
	);
};

RichTextField.displayName = "Editor";

export default RichTextField;

interface RichTextFieldProps {
	className?: string;
	placeholder?: string;
	name: string;
	height?: string;
	value?: string;
	success?: string;
	error?: string;
	readOnly?: boolean;
	onBlur?(html: string): void;
	maxLength?: number;
}
